import { Injectable } from '@angular/core';
import { BehaviorSubject, first, Observable, of, take } from 'rxjs';
import { LgvLoanApp } from '../../../data-models/lgv-loan-application.model';
import { LgvLoanApplicationService } from '../lgv-loan-application.service';
import { LgvClientService, NotificationService } from '../../core.module';
import { LgvAuthMgrService } from '../lgv-auth-mgr.service';
import { HttpClient } from '@angular/common/http';
import { EConsentDialogComponent } from '../../../shared/loan-app/e-consent-dialog/e-consent-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { environment as env } from '../../../../environments/environment';
import { Router } from '@angular/router';


@Injectable({
    providedIn: 'root'
})
export class LoanAppPageService {

    _selectedLoanApplication: LgvLoanApp.LoanApplicationDetail = null;
    selectedLoanApplication$ = new BehaviorSubject(this._selectedLoanApplication);

    _loanActivities: LgvLoanApp.LoanAppActivityLog[] = [];
    loanActivities$ = new BehaviorSubject(this._loanActivities);

    _loanAppId: string = null;
    loanAppId$ = new BehaviorSubject(this._loanAppId);

    get loanAppStatus(): LgvLoanApp.LoanStatus { return this.selectedLoanApplication$.value?.loanTimeline?.activeStatus }
    get isPreApplicationMode(): boolean { return this.loanAppStatus && this.loanAppStatus.code === 'PreApplication'; }
    get isSubmittedCanStillEditMode(): boolean { return this.loanAppStatus && !this.isPreApplicationMode && this.loanAppStatus.code.startsWith('Pre'); }
    get isReadOnlyMode(): boolean { return this.loanAppStatus && !this.isPreApplicationMode && !this.isSubmittedCanStillEditMode; }

    public isLoading$ = new BehaviorSubject(true);
    public isLoanFunded$ = new BehaviorSubject(false);

    constructor(private lgvLoanAppSvc: LgvLoanApplicationService,
        private notificationSvc: NotificationService,
        private lgvAuthMgrSvc: LgvAuthMgrService,
        private lgvClientSvc: LgvClientService,
        private loanAppSvc: LgvLoanApplicationService,
        private http: HttpClient,
        private matDialog: MatDialog,
        private router: Router) { }

    loadLoanApp(appId: string) {
        this.isLoading$.next(true);
        this.loanAppId$.next(appId);
        const fetchLoanApp = this.lgvAuthMgrSvc.IsClient ? this.lgvLoanAppSvc.fetchThisLoanApp(appId, this.lgvClientSvc.ClientID$.value) : this.lgvLoanAppSvc.fetchThisLoanApp(appId);
        fetchLoanApp.pipe(take(1)).subscribe(x => {
            this.selectedLoanApplication$.next(x);
            this.isLoading$.next(false);

            let fundingStatus = x.loanTimeline?.loanStatuses?.find(ls => ls.code === 'Funding');

            if (fundingStatus && fundingStatus.isComplete === true) {
                this.isLoanFunded$.next(true);
            }

        },
            err => {
                console.log(err);
                this.notificationSvc.error('Unable to load Loan Application');
                this.isLoading$.next(false);
            }
        );
    }

    loadLoanActivities(appId: string) {
        this.lgvLoanAppSvc.fetchLoanAppActivities(appId).pipe(take(1)).subscribe((activityLog) => {
            this.loanActivities$.next(activityLog);
        })
    }

    openEconsentDialog(linkToNavigate, appID, isClient) {
        this.loanAppSvc.fetchThisLoanApp(appID, this.lgvAuthMgrSvc?.ClientInfo?.id).pipe(take(1)).subscribe(loanApp => {
            const businessConsent = loanApp.loanInfo?.customFields?.find(cf => cf?.code == 'EConsentToDoBusinessElectronicallyAccepted');
            const dateConsentGiven = loanApp.loanInfo.customFields?.find(x => x.code === 'DateConsentGiven');
            const clientEconsent = loanApp.loanInfo.customFields?.find(x => x.code === 'ClientEConsentStatus');
            const consentToMessages = loanApp.loanInfo.customFields?.find(x => x.code === 'EConsentToReceiveMessagesAccepted');
            if (!dateConsentGiven?.value || !businessConsent || businessConsent?.value == 'false' || (isClient && (!clientEconsent ||  clientEconsent?.value == 'Pending'))) {
                const econsentDialogRef = this.matDialog.open(EConsentDialogComponent, {
                    data: {
                        businessConsentValue: businessConsent?.value === 'true',
                        consentToMessagesValue: consentToMessages?.value == 'true',
                        isAgent: !isClient
                    },
                    width: '360px'
                });
                econsentDialogRef.afterClosed().pipe(take(1)).subscribe((action) => {
                    if (action?.consentToDoBusiness) {
                        this.saveEconsent(action, loanApp, linkToNavigate, appID, isClient);
                    }
                });
            }
            else {
                this.router.navigate([linkToNavigate]);
            }
        });
    }

    saveEconsent(data, loanAppModel, linkToNavigate, appID, isClient) {

        const businessConsentCustomField = this.loanAppSvc.customFields$.value?.find(x => x.code === 'EConsentToDoBusinessElectronicallyAccepted');
        if(!!businessConsentCustomField) {
            businessConsentCustomField.value = 'true';
            if (!loanAppModel.loanInfo.customFields || loanAppModel.loanInfo.customFields?.length == 0) {
                loanAppModel.loanInfo.customFields = [businessConsentCustomField];
            } else {
                loanAppModel.loanInfo.customFields.push(businessConsentCustomField);
            }
        }

        const dateConsentGivenCustomField = this.loanAppSvc.customFields$.value?.find(x => x.code === 'DateConsentGiven');
        if(!!dateConsentGivenCustomField) {
            dateConsentGivenCustomField.value = data.date ? data.date : (new Date()).toISOString();
            loanAppModel.loanInfo.customFields.push(dateConsentGivenCustomField);
        }

        if (data?.consentToReceiveMessages) {
            const receiveMessagesCustomField = this.loanAppSvc.customFields$.value?.find(x => x.code === 'EConsentToReceiveMessagesAccepted');
            if(!!receiveMessagesCustomField) {
                receiveMessagesCustomField.value = 'true';
                loanAppModel.loanInfo.customFields.push(receiveMessagesCustomField);
            }
        }

        const clientEconsentCustomField = this.loanAppSvc.customFields$.value?.find(x => x.code === 'ClientEConsentStatus');

        if(!!clientEconsentCustomField) {
            const clientEconsentIndex = loanAppModel.loanInfo.customFields?.findIndex(x => x.code === 'ClientEConsentStatus');
            if(!isClient && loanAppModel.loanInfo.customFields[clientEconsentIndex]?.value != 'Accepted') {
                clientEconsentCustomField.value = 'Pending';
                loanAppModel.loanInfo.customFields.push(clientEconsentCustomField);
            } else {
                if(loanAppModel.loanInfo.customFields[clientEconsentIndex]?.value == 'Pending') {
                    loanAppModel.loanInfo.customFields[clientEconsentIndex].value = 'Accepted';
                } else if(clientEconsentIndex < 0) {
                    clientEconsentCustomField.value = 'Accepted';
                    loanAppModel.loanInfo.customFields.push(clientEconsentCustomField);
                }
            }
        }
        

        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${appID}/save`;
        if (isClient) {
            bldUrl = bldUrl + `?clientId=${this.lgvClientSvc.ClientID$.value}`;
        }
        this.http.put<any>(bldUrl, loanAppModel).pipe(first()).subscribe(x => {
            this.router.navigate([linkToNavigate]);
        }, (err) => {
            console.log(err);
        });
    }

}
